/**
 * Server calls for spaces and their teams (RESTORE_PLAN R1.5): the overview (readiness per space
 * and the company's request types), a space edit, and one team at a time.
 */
import { projectId } from './supabase/info';
import type { SpaceEditDraft, SpaceReadiness, TeamRequestTypeRef } from './spaceTeams';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export interface SpacesOverview {
  readiness: Record<string, SpaceReadiness>;
  requestTypes: TeamRequestTypeRef[];
}

async function send<T>(url: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${accessToken}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

const spacePath = (companyId: string, spaceId: string) => `${BASE}/spaces/${encodeURIComponent(companyId)}/${encodeURIComponent(spaceId)}`;

export function loadSpacesOverview(companyId: string, accessToken: string): Promise<SpacesOverview> {
  return send<SpacesOverview>(`${BASE}/spaces/${encodeURIComponent(companyId)}/overview`, accessToken).then((d) => ({
    readiness: d.readiness || {},
    requestTypes: Array.isArray(d.requestTypes) ? d.requestTypes : [],
  }));
}

export function updateSpace(companyId: string, spaceId: string, changes: Partial<SpaceEditDraft>, accessToken: string) {
  return send<{ space: Record<string, unknown> }>(spacePath(companyId, spaceId), accessToken, { method: 'PUT', body: JSON.stringify(changes) });
}

export function addSpaceTeam(companyId: string, spaceId: string, team: { name: string; description: string }, accessToken: string) {
  return send<{ space: Record<string, unknown> }>(`${spacePath(companyId, spaceId)}/teams`, accessToken, { method: 'POST', body: JSON.stringify(team) });
}

export function editSpaceTeam(companyId: string, spaceId: string, originalName: string, team: { name: string; description: string }, accessToken: string) {
  return send<{ space: Record<string, unknown>; requestTypesUpdated: number }>(`${spacePath(companyId, spaceId)}/teams`, accessToken, {
    method: 'PUT',
    body: JSON.stringify({ originalName, ...team }),
  });
}

export function removeSpaceTeam(companyId: string, spaceId: string, name: string, accessToken: string) {
  return send<{ space: Record<string, unknown>; requestTypesUpdated: number }>(`${spacePath(companyId, spaceId)}/teams?name=${encodeURIComponent(name)}`, accessToken, { method: 'DELETE' });
}
